import React from 'react';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import { Dispatch } from 'redux';
import { Route, RouteComponentProps } from 'react-router';
import { NavLink, withRouter } from 'react-router-dom';
import { withTranslation, WithTranslation, Trans } from 'react-i18next';
import { Url, CHECKOUT_IDS } from '@wix/da-url';
import Button, {
  ProfileOwnerWatchButton,
} from '@wix/da-shared-react/pkg/Button';
import { Nav } from '@wix/da-shared-react/pkg/a11y/headings/tagWrappers';
import Heading from '@wix/da-shared-react/pkg/a11y/headings/Heading';
import VisuallyHidden from '@wix/da-shared-react/pkg/a11y/VisuallyHidden';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import GiftIcon from '@wix/da-shared-react/pkg/Icons/Gift';
import MailIcon from '@wix/da-shared-react/pkg/Icons/24x24/Mail';
import ShareIcon from '@wix/da-shared-react/pkg/Icons/24x24/Share';
import CoreIconSmall from '@wix/da-shared-react/pkg/Icons/CoreIconSmall';
import LlamaIcon from '@wix/da-shared-react/pkg/Icons/Llama';
import CakeIcon from '@wix/da-shared-react/pkg/Icons/24x24/Cake';
import PointsIcon from '@wix/da-shared-react/pkg/Icons/Points';
import PageIcon from '@wix/da-shared-react/pkg/Icons/Page';
import EllipsisIcon from '@wix/da-shared-react/pkg/Icons/Ellipsis';
import EyeIcon from '@wix/da-shared-react/pkg/Icons/Eye';
import BlockIcon from '@wix/da-shared-react/pkg/Icons/24x24/Block';
import {
  withFeatureFlags,
  WithFeatureFlagsProps,
} from '@wix/da-shared-react/pkg/redux/flags';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Popover/TextTooltipOnHover';
import { ThemeOptions } from '@wix/da-shared-react/pkg/publicSession/types';
import MiniProfileIdentity from '../../../UserWidgets/_partials/MiniProfileIdentity';
import { givableBadgeItemsHash } from '../../../UserWidgets/MyBadges/Badges/BadgeGifts/givableBadgeItems';
import WatchButtonConneсtContainer from '../../WatchButton.container';
import {
  withVerification,
  WithVerificationProps,
} from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import {
  BiDataHook,
  BiSpan,
  BiLink,
  BiEventType,
  ReferralType,
} from '@wix/da-shared-react/pkg/biLogger';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { DesktopSharePopup } from '@wix/da-shared-react/pkg/SharePopup';
import PopoverWithTooltip from '@wix/da-shared-react/pkg/ActionBar/_partials/PopoverWithTooltip';
import HiddenIcon from '@wix/da-shared-react/pkg/Icons/Hidden';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import { SubNavBarProps } from '../../SubNavBar';
import CoreSaleTooltip from './CoreSaleTooltip';
import s from '../SubNavBarDesktop.scss';

const SCROLL_THROTTLE_TIMEOUT = 200;
const WatchButton = WatchButtonConneсtContainer(ProfileOwnerWatchButton);

const noop = () => {};

export interface Props extends SubNavBarProps {
  isSticky: boolean;
  isPromoActive: boolean;
  canViewAsVisitor: boolean;
  canRespondToGroupNotifications: boolean;
  viewAsVisitor: (toggle: boolean) => void;
  showBlockModal: () => void;
}

class SubNavBarInner extends React.PureComponent<
  Props &
    WithTranslation & { dispatch: Dispatch<any> } & RouteComponentProps<any> &
    WithVerificationProps &
    WithFeatureFlagsProps
> {
  containerRef: React.RefObject<HTMLDivElement> = React.createRef();
  userInfoRef: React.RefObject<HTMLDivElement> = React.createRef();
  navListRef: React.RefObject<HTMLDivElement> = React.createRef();
  giveRef: React.RefObject<HTMLDivElement> = React.createRef();
  messageRef: React.RefObject<HTMLDivElement> = React.createRef();
  shareRef: React.RefObject<HTMLDivElement> = React.createRef();
  moreActionsRef: React.RefObject<HTMLDivElement> = React.createRef();
  watchRef: React.RefObject<HTMLDivElement> = React.createRef();

  state = {
    hideNavItemsCount: 0,
    hideGiveItems: false,
    hideMessageBtn: false,
    hideShareBtn: false,
  };

  widthCache = {
    userInfoWidth: 0,
    moreActionsWidth: 0,
    giveWidth: 0,
    messageWidth: 0,
    shareWidth: 0,
    watchWidth: 0,
    navLinksWidth: [],
  };

  componentDidMount() {
    this.setWidthCache();
    this.updateVisibleItems();
    window.addEventListener('resize', this.updateVisibleItems);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.isSticky !== nextProps.isSticky) {
      this.updateVisibleItems();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateVisibleItems);
  }

  setWidthCache = () => {
    this.widthCache.moreActionsWidth = parseInt(s['more-actions-button-width']);
    this.widthCache.giveWidth = this.giveRef.current!.getBoundingClientRect().width;
    this.widthCache.shareWidth = this.shareRef.current!.getBoundingClientRect().width;
    this.widthCache.messageWidth = this.messageRef.current
      ? this.messageRef.current.getBoundingClientRect().width
      : 0;
    this.widthCache.watchWidth = this.watchRef.current
      ? this.watchRef.current.getBoundingClientRect().width
      : 0;
    this.widthCache.navLinksWidth = Array.prototype.map.call(
      this.navListRef.current!.children,
      item => item.getBoundingClientRect().width
    );
    this.widthCache.messageWidth = this.messageRef.current
      ? this.messageRef.current.getBoundingClientRect().width
      : 0;
  };

  updateVisibleItems = throttle(
    () => {
      if (!this.containerRef.current || !this.userInfoRef.current) {
        return;
      }

      const {
        navLinksWidth,
        moreActionsWidth,
        giveWidth,
        messageWidth,
        shareWidth,
        watchWidth,
      } = this.widthCache;
      // We need to hide elements if there is no enough space
      // so we calculate available space reducing static items width from the full container width
      // first we try to fit navigation link items (first priority due to specs)
      // then goes "Give me" button, then "Message"

      const fullWidth = this.containerRef.current!.getBoundingClientRect()
        .width;

      const userInfoWidth = this.userInfoRef.current!.getBoundingClientRect()
        .width;

      let availableWidth =
        fullWidth - userInfoWidth - moreActionsWidth - watchWidth;

      let hideNavItemsCount = 0;

      // if we already cannot fit the item - do not try to fit others coming after it
      let hideTheRest = false;
      navLinksWidth.forEach((itemWidth, i) => {
        if (!hideTheRest && itemWidth < availableWidth) {
          availableWidth -= itemWidth;
        } else {
          hideNavItemsCount += 1;
          hideTheRest = true;
        }
      });

      const hideGiveItems = hideTheRest || giveWidth > availableWidth;
      if (!hideGiveItems) {
        availableWidth -= giveWidth;
      }
      const hideShareBtn = hideTheRest || shareWidth > availableWidth;
      if (!hideShareBtn) {
        availableWidth -= shareWidth;
      }
      const hideMessageBtn = hideTheRest || messageWidth > availableWidth;

      if (
        hideNavItemsCount !== this.state.hideNavItemsCount ||
        hideGiveItems !== this.state.hideGiveItems ||
        hideMessageBtn !== this.state.hideMessageBtn ||
        hideShareBtn !== this.state.hideShareBtn
      ) {
        this.setState({
          hideNavItemsCount,
          hideGiveItems,
          hideMessageBtn,
          hideShareBtn,
        });
      }
    },
    SCROLL_THROTTLE_TIMEOUT,
    { leading: true }
  );

  render() {
    const {
      t,
      profileOwnerUser,
      isOwner,
      routes,
      isSticky,
      biClickProfileNavBar,
      isViewingAsVisitor,
      flags,
    } = this.props;
    const { hideNavItemsCount } = this.state;

    const routesToRender = hideNavItemsCount
      ? routes.slice(0, -hideNavItemsCount)
      : routes;

    return (
      <Route
        key="nav-user"
        path="/:username"
        sensitive={true}
        render={({ match }) => (
          <Nav
            className={classnames(
              s['navbar'],
              isOwner && s['owner'],
              isSticky && s['sticky']
            )}
            ref={this.containerRef}
            data-hook={BiDataHook.PROFILE_NAV}
          >
            <VisuallyHidden>
              <Heading>{t('navigation.subnavigation.heading')}</Heading>
            </VisuallyHidden>
            <div ref={this.userInfoRef} className={s['navbar-user-info']}>
              <MiniProfileIdentity
                user={profileOwnerUser}
                className={s['user-info']}
                usernameClassName={s['user-name']}
                withTooltip={false}
              />
            </div>

            <div
              className={classnames(
                s['nav-list'],
                isSticky && s['with-separator']
              )}
              ref={this.navListRef}
            >
              {routesToRender.map(route => (
                <NavLink
                  key={route.key}
                  to={(route.path || '')
                    .replace(/:username/, match.params.username)
                    .replace(/\/:[a-z0-9_()\\+]+\?/gi, '')}
                  exact={route.exact}
                  className={s['nav-item']}
                  activeClassName={s['active']}
                  onClick={() => biClickProfileNavBar(route.key)}
                >
                  {route.key === 'stats' && (
                    <TextTooltipOnHover
                      // prettier-ignore
                      tooltipText={(
                        <Trans i18nKey="navigation.route.stats.onlyYou">
                          Other Deviants Can’t<br />See Your Statistics.
                        </Trans>
                      )}
                      className={s['nav-item-icon']}
                    >
                      {/** Only need this icon on one route, if we need more icons, we should add a field to `route` */}
                      <HiddenIcon
                        size={IconSize.TINY}
                        aria-label={t('navigation.route.stats.a11y.private')}
                      />
                    </TextTooltipOnHover>
                  )}
                  {t(`navigation.route.${route.key}`)}
                  {route.isBeta && <span className={s['beta']}>beta</span>}
                </NavLink>
              ))}
            </div>
            {this.renderMoreActionsIconDropdown(match)}
            {this.renderShareButton()}
            {this.renderGiveMenu()}
            {flags.includes('start_chat_button')
              ? this.renderStartChatButton()
              : this.renderSendMessageButton()}
            <div
              ref={this.watchRef}
              className={classnames(isOwner && s['hidden'])}
            >
              <WatchButton
                className={s['watch-btn']}
                disableClickHandler={isViewingAsVisitor}
                useV2Styles={false}
              />
            </div>
          </Nav>
        )}
      />
    );
  }

  renderMoreActionsIconDropdown = match => {
    const menuItems = this.getMoreActionsMenuItems(match);
    if (!menuItems.length) {
      return null;
    }
    const hideGiveItems = this.state.hideGiveItems || undefined;
    const { initGiveState, isViewingAsVisitor } = this.props;

    return (
      <div ref={this.moreActionsRef} className={s['more-actions']}>
        <IconButtonWithContextMenu
          popperClassName={s['more-actions-popper']}
          items={menuItems}
          onOpen={hideGiveItems && initGiveState}
          disabled={isViewingAsVisitor}
          withButton
        >
          <EllipsisIcon withPadding className={s['icon']} />
        </IconButtonWithContextMenu>
      </div>
    );
  };

  getMoreActionsMenuItems = match => {
    // hide means hide in the more actions dropdown
    const { hideGiveItems, hideMessageBtn, hideNavItemsCount } = this.state;
    const {
      t,
      routes,
      isLoggedIn,
      isOwner,
      isVerificationNeeded,
      pushVerificationPopup,
      profileOwnerUser,
      canViewAsVisitor,
      canRespondToGroupNotifications,
      showBlockModal,
    } = this.props;

    const menuItems = [] as any;
    const standardActionsGroup = { items: [] as any };

    if (hideMessageBtn) {
      standardActionsGroup.items.push({
        icon: <MailIcon />,
        label: t(`navigation.subnavigation.message`),
        onClick: isVerificationNeeded
          ? () => pushVerificationPopup('notes')
          : undefined,
        link: isVerificationNeeded
          ? undefined
          : Url.userNoteLink(profileOwnerUser.username),
      });
    }

    if (standardActionsGroup.items.length) {
      menuItems.push(standardActionsGroup);
    }

    if (hideNavItemsCount > 0) {
      const pageLinksGroup = {
        label: t('navigation.subnavigation.pages'),
        items: routes.slice(-hideNavItemsCount).map(route => ({
          icon: <PageIcon />,
          label: t(`navigation.route.${route.key}`),
          link: (route.path || '')
            .replace(/:username/, match.params.username)
            .replace(/\/:[a-z0-9_()\\+]+\?/gi, ''),
        })),
      };
      menuItems.push(pageLinksGroup);
    }

    if (hideGiveItems && !isOwner) {
      const giveItemsGroup = {
        items: this.getGiveMenuItems(),
      };
      menuItems.push(giveItemsGroup);
    }

    if (canViewAsVisitor && isOwner) {
      menuItems.push({
        icon: <EyeIcon />,
        label: t('navigation.subnavigation.viewAsVisitor'),
        onClick: this.viewAsVisitor,
        biData: { evid: BiEventType.VIEW_AS_VISITOR },
      });
    }

    if (isLoggedIn && !isOwner) {
      menuItems.push({
        icon: <BlockIcon />,
        label: t('navigation.subnavigation.blockUser'),
        onClick: showBlockModal,
      });
    }

    if (isGroup(profileOwnerUser) && canRespondToGroupNotifications) {
      menuItems.push({
        icon: <MailIcon />,
        label: t('navigation.subnavigation.viewNotifications'),
        onClick: () =>
          window.location.assign(
            `${Url.feedbackLink()}?folder=${profileOwnerUser.userId}`
          ),
      });
    }

    return menuItems;
  };

  renderGiveMenu = () => {
    const {
      t,
      isOwner,
      isLoggedIn,
      profileOwnerUser,
      initGiveState,
      restrictLoggedOut,
      isViewingAsVisitor,
      isPromoActive,
    } = this.props;
    const { hideGiveItems } = this.state;
    const biData = {
      evid: BiEventType.GIFT_CLICK,
      recipientid: profileOwnerUser.useridUuid,
    };

    const giftIcon = (
      <GiftIcon
        className={classnames(s['icon'], !isLoggedIn && s['icon_hover'])}
      />
    );
    const icon = isPromoActive ? (
      giftIcon
    ) : (
      <TextTooltipOnHover tooltipText={t('common.give')}>
        {giftIcon}
      </TextTooltipOnHover>
    );
    return (
      <div
        ref={this.giveRef}
        className={classnames(
          s['give-items'],
          (hideGiveItems || isOwner) && s['hidden']
        )}
      >
        {isLoggedIn ? (
          <div className={s['icon-wrapper']}>
            <IconButtonWithContextMenu
              withButton={true}
              className={s['gift-button']}
              contextMenuClassName={s['dropdown-menu']}
              items={this.getGiveMenuItems()}
              onOpen={initGiveState}
              biData={biData}
              disabled={isViewingAsVisitor}
            >
              {icon}
            </IconButtonWithContextMenu>
            <CoreSaleTooltip className={s['core-sale-tooltip']} />
          </div>
        ) : (
          <BiLink
            onClick={() =>
              restrictLoggedOut({ restrictedActionName: ReferralType.GIVE })
            }
            biData={biData}
          >
            {icon}
          </BiLink>
        )}
      </div>
    );
  };

  getGiveMenuItems = () => {
    const {
      t,
      pushBadgeModal,
      pushPointsModal,
      profileOwnerUser: { username, userId, useridUuid },
      giveState,
      onLlamaBadgeSent,
    } = this.props;

    if (!giveState) {
      return [];
    }

    const coreLabel = giveState.canBeGivenCore
      ? t('navigation.subnavigation.giveCore')
      : t('navigation.subnavigation.hasCore');
    const pointsLabel = t('navigation.subnavigation.givePoints');
    const lamaLabel = giveState.canBeGivenLlama
      ? t('navigation.subnavigation.giveLlamaBadge')
      : t('navigation.subnavigation.hasGotLlamaBadge');
    const cakeLabel = giveState.canBeGivenCake
      ? t('navigation.subnavigation.giveCakeBadge')
      : t('navigation.subnavigation.hasGotCakeBadge');
    const biData = {
      evid: BiEventType.GIFT_SELECT_CLICK,
      recipientid: useridUuid,
    };
    const giveCoreUrl = Url.giveCoreMembership(username);

    return [
      {
        icon: giveState.isLoading ? <LoadingIndicator /> : <CoreIconSmall />,
        iconClassName: classnames(giveState.isLoading && s['loading-spinner']),
        label: coreLabel,
        componentClass: BiSpan,
        componentProps: {
          biData: {
            ...biData,
            menu_item_name: coreLabel,
            link_url: giveCoreUrl,
          },
        },
        onClick: () => window.location.assign(giveCoreUrl),
        disabled: !giveState.canBeGivenCore,
      },
      {
        icon: <PointsIcon />,
        label: pointsLabel,
        componentClass: BiSpan,
        componentProps: {
          biData: { ...biData, menu_item_name: pointsLabel },
        },
        onClick: () => {
          pushPointsModal(username, pointsLabel);
        },
      },
      {
        icon: giveState.isLoading ? <LoadingIndicator /> : <LlamaIcon />,
        iconClassName: classnames(giveState.isLoading && s['loading-spinner']),
        label: lamaLabel,
        componentClass: BiSpan,
        componentProps: {
          biData: { ...biData, menu_item_name: lamaLabel },
        },
        onClick: () => {
          const badge = givableBadgeItemsHash.llama;
          pushBadgeModal(
            'llama',
            userId,
            username,
            t('widgets.badgesbox.gift.give', { title: badge.title }),
            onLlamaBadgeSent
          );
        },
        disabled: !giveState.canBeGivenLlama,
      },
      {
        icon: giveState.isLoading ? <LoadingIndicator /> : <CakeIcon />,
        iconClassName: classnames(giveState.isLoading && s['loading-spinner']),
        label: cakeLabel,
        componentClass: BiSpan,
        componentProps: {
          biData: { ...biData, menu_item_name: cakeLabel },
        },
        onClick: () => {
          window.location.assign(
            `${Url.checkoutLink(CHECKOUT_IDS.CAKE)}&foruserid=${userId}`
          );
        },
        disabled: !giveState.canBeGivenCake,
      },
    ];
  };

  renderSendMessageButton() {
    const {
      t,
      profileOwnerUser,
      isOwner,
      isLoggedIn,
      isVerificationNeeded,
      isViewingAsVisitor,
      restrictLoggedOut,
      pushVerificationPopup,
    } = this.props;
    const { hideMessageBtn } = this.state;
    if (isOwner || hideMessageBtn) {
      return null;
    }

    const biData = {
      evid: BiEventType.SEND_NOTE_CLICK,
      recipientid: profileOwnerUser.useridUuid,
    };

    let onClick: (() => void) | undefined = undefined;
    if (isViewingAsVisitor) {
      onClick = noop;
    } else if (!isLoggedIn) {
      onClick = () =>
        restrictLoggedOut({ restrictedActionName: ReferralType.NOTE });
    } else if (isVerificationNeeded) {
      onClick = () => pushVerificationPopup('notes');
    }
    const href = Url.userNoteLink(profileOwnerUser.username);
    return (
      <div ref={this.messageRef} className={s['with-separator']}>
        <Button
          className={s['message-btn']}
          view="b2"
          biData={biData}
          {...(onClick && { onClick })}
          {...(!onClick && { href })}
        >
          {t(`navigation.subnavigation.message`)}
        </Button>
      </div>
    );
  }

  renderStartChatButton() {
    const {
      t,
      profileOwnerUser,
      isOwner,
      isLoggedIn,
      isVerificationNeeded,
      isViewingAsVisitor,
      restrictLoggedOut,
      pushVerificationPopup,
      onStartChat,
    } = this.props;
    const { hideMessageBtn } = this.state;
    if (isOwner || hideMessageBtn) {
      return null;
    }

    const biData = {
      evid: BiEventType.SEND_CHAT_BUTTON_CLICK,
      typeid: ResourceType.USER,
      itemid: profileOwnerUser.userId,
      friend_id: profileOwnerUser.useridUuid,
    };

    let onClick: (() => void) | undefined = undefined;
    if (isViewingAsVisitor) {
      onClick = noop;
    } else if (!isLoggedIn) {
      onClick = () =>
        restrictLoggedOut({ restrictedActionName: ReferralType.CHAT });
    } else if (isVerificationNeeded) {
      onClick = () => pushVerificationPopup('chat');
    } else {
      onClick = () => onStartChat(profileOwnerUser);
    }

    return (
      <div ref={this.messageRef} className={s['with-separator']}>
        <Button
          className={s['message-btn']}
          view="b2"
          biData={biData}
          onClick={onClick}
        >
          {t(`common.chat`)}
        </Button>
      </div>
    );
  }

  renderShareButton() {
    const { t, profileOwnerUser } = this.props;
    const { hideShareBtn } = this.state;
    if (hideShareBtn) {
      return null;
    }
    return (
      <BiSpan
        biData={{
          evid: BiEventType.CLICK_SHARE,
        }}
        onClick={() => {
          return null;
        }}
      >
        <span ref={this.shareRef}>
          <PopoverWithTooltip
            tooltip={t('navigation.subnavigation.share')}
            forceTheme={ThemeOptions.DARK}
            positionOnly
            placement="bottom-end"
            referenceClassName={s['share-btn']}
            renderPopover={() => (
              <DesktopSharePopup
                url={Url.userLink(profileOwnerUser.username)}
                title={t!(
                  'navigation.subnavigation.share.title',
                  profileOwnerUser
                )}
              />
            )}
            renderChildren={({ ref }) => (
              <IconButton className={classnames(s['icon'], s['icon_hover'])}>
                <ShareIcon containerRef={ref} />
              </IconButton>
            )}
          />
        </span>
      </BiSpan>
    );
  }

  viewAsVisitor = () => {
    this.props.viewAsVisitor(true);
  };
}

export default withFeatureFlags(
  withRouter(withVerification(withTranslation()(SubNavBarInner)))
);
